/**
 * Repo context — single source of truth for "which managed repo is the
 * operator looking at right now". Replaces the per-loader `.limit(1)` of
 * `repos` pattern that left every page silently scoped to whichever row
 * Supabase returned first.
 *
 * Two pieces in one file because they're the same concern:
 * - `ReposProvider` loads the full list once on auth + caches it (a
 *   React context exposes `repos: RepoSummary[]` + `refresh()`).
 * - The same provider holds the active-repo state, persists it to
 *   `localStorage['repo:active']`, and exposes a setter that triggers a
 *   `useRevalidator()` so every visible loader refetches in the new
 *   scope.
 *
 * Loaders run outside React, so they can't read context. They use the
 * exported `getActiveRepoId(supabase)` helper instead — URL `?repo=<id>`
 * > localStorage > first-repo-from-Supabase. The async fallback is what
 * lets the very first navigation (cold load, empty localStorage)
 * resolve without crashing.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useRevalidator } from 'react-router';

import { getSupabase, type SupabaseBrowserClient } from './supabase';
import { useSession } from './supabase-context';

const STORAGE_KEY = 'repo:active';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface RepoSummary {
  id: string;
  owner: string;
  name: string;
  last_active_at: string | null;
}

interface ReposCtxValue {
  /** All repos the operator can see (RLS-gated). Empty until loaded. */
  repos: RepoSummary[];
  /** True while the first repos.list fetch is in flight. */
  loading: boolean;
  /** Currently selected repo id, or null if none picked yet. */
  activeRepoId: string | null;
  /** Convenience: full row for `activeRepoId`. */
  activeRepo: RepoSummary | null;
  /** Switches the active repo + persists + revalidates all loaders. */
  setActiveRepoId: (id: string) => void;
  /** Re-fetch the list (e.g., after onboarding a new repo). */
  refresh: () => Promise<void>;
}

const ReposCtx = createContext<ReposCtxValue | null>(null);

function readStoredActive(): string | null {
  if (typeof window === 'undefined') return null;
  const fromLs = window.localStorage.getItem(STORAGE_KEY);
  return fromLs && UUID_RE.test(fromLs) ? fromLs : null;
}

function readUrlActive(): string | null {
  if (typeof window === 'undefined') return null;
  const fromUrl = new URL(window.location.href).searchParams.get('repo');
  return fromUrl && UUID_RE.test(fromUrl) ? fromUrl : null;
}

/**
 * Synchronous best-effort: URL query > localStorage. Loaders prefer
 * `getActiveRepoId()` (the async variant below) so they also pick up
 * the Supabase fallback on cold start; but call sites that already
 * know a repo list (e.g. inside a component with the provider) can
 * use this.
 */
export function getActiveRepoIdSync(): string | null {
  return readUrlActive() ?? readStoredActive();
}

/**
 * Loader-side helper. Resolution order:
 *   1. `?repo=<uuid>` in the current URL — wins for bookmarks/deep links.
 *   2. `localStorage['repo:active']` — the operator's last picked repo.
 *   3. First repo from Supabase (ordered by `last_active_at` desc).
 *
 * If the user has zero managed repos visible to them, returns null.
 * Loaders should treat null as "render an empty state with a hint to
 * onboard a repo" rather than throwing.
 */
export async function getActiveRepoId(
  supabase: SupabaseBrowserClient,
): Promise<string | null> {
  const synced = getActiveRepoIdSync();
  if (synced) return synced;
  const { data } = await supabase
    .from('repos')
    .select('id')
    .order('last_active_at', { ascending: false, nullsFirst: false })
    .limit(1);
  const id = data?.[0]?.id ?? null;
  if (id && typeof window !== 'undefined') {
    // Cache the fallback so subsequent loaders skip the Supabase round-trip.
    window.localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

/**
 * Provider — mount once near the top of the tree (above `<RootLayout>`
 * but below `<SupabaseProvider>` so `useSession()` works).
 */
export function ReposProvider({ children }: { children: ReactNode }) {
  const supabase = useMemo(() => getSupabase(), []);
  const { status } = useSession();
  const revalidator = useRevalidator();

  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeRepoId, setActiveRepoIdState] = useState<string | null>(
    () => readUrlActive() ?? readStoredActive(),
  );

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('repos')
      .select('id, owner, name, last_active_at')
      .order('last_active_at', { ascending: false, nullsFirst: false });
    if (!error && data) {
      setRepos(data as RepoSummary[]);
      // If nothing is active yet, pick the first row + persist it. This
      // makes the cold-start case "open / and the dashboard shows the
      // first repo" rather than "open / and see a blank repo chip".
      if (data.length > 0) {
        const existing = readUrlActive() ?? readStoredActive();
        if (!existing) {
          const firstId = (data[0] as RepoSummary).id;
          window.localStorage.setItem(STORAGE_KEY, firstId);
          setActiveRepoIdState(firstId);
        } else if (existing !== activeRepoId) {
          setActiveRepoIdState(existing);
        }
      }
    }
    setLoading(false);
  }, [supabase, activeRepoId]);

  useEffect(() => {
    if (status !== 'authed') {
      setRepos([]);
      setLoading(false);
      return;
    }
    void load();
    // load is stable enough; we only want to refire on auth flips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const setActiveRepoId = useCallback(
    (id: string) => {
      if (!UUID_RE.test(id)) return;
      window.localStorage.setItem(STORAGE_KEY, id);
      setActiveRepoIdState(id);
      // Re-run every visible loader so the page reflects the new scope.
      // No URL rewrite — pages that want the param ship a Link with it.
      revalidator.revalidate();
    },
    [revalidator],
  );

  const activeRepo = useMemo(
    () => repos.find((r) => r.id === activeRepoId) ?? null,
    [repos, activeRepoId],
  );

  const value: ReposCtxValue = {
    repos,
    loading,
    activeRepoId,
    activeRepo,
    setActiveRepoId,
    refresh: load,
  };

  return <ReposCtx.Provider value={value}>{children}</ReposCtx.Provider>;
}

export function useRepos(): ReposCtxValue {
  const ctx = useContext(ReposCtx);
  if (!ctx) throw new Error('useRepos must be used inside <ReposProvider>');
  return ctx;
}
